import React from "react";
import {
  Typography,
  Button,
  Box,
  Paper,
  Fade,
  Stack,
  Card,
  CardContent,
  IconButton,
  Divider,
  Zoom,
  Grid,
} from "@mui/material";
import { Link } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import UploadIcon from "@mui/icons-material/Upload";
import TemplateIcon from "@mui/icons-material/Description";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import ImageIcon from "@mui/icons-material/Image";
import PersonIcon from "@mui/icons-material/Person";
import SendIcon from "@mui/icons-material/Send";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MailIcon from "@mui/icons-material/Mail";
import logo from "../Image/9rlogo.png";

export default function HomePage() {
  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 180px)",
        background: "linear-gradient(135deg, #e0e7ff 0%, #d1fae5 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 2, md: 4 },
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Fade in timeout={1000}>
        <Box sx={{ maxWidth: 1200, mx: "auto", my:2, px: { xs: 1, md: 2 }, position: "relative", zIndex: 1 }}>
          <Paper
            elevation={4}
            sx={{
              p: { xs: 3, md: 5 },
              maxWidth: 850,
              mx: "auto",
              textAlign: "center",
              borderRadius: 2,
              mb: { xs: 3, md: 5 },
              background: "linear-gradient(135deg, #ffffff 0%, #eef2ff 100%)",
              border: "1px solid rgba(102, 126, 234, 0.2)",
              boxShadow: "0 10px 20px rgba(0, 0, 0, 0.06)",
              "&:hover": {
                boxShadow: "0 12px 30px rgba(0, 0, 0, 0.1)",
                transform: "translateY(-3px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 4 }}>
              <Zoom in timeout={1200}>
                <Box component="img" src={logo} alt="Logo" sx={{ height: 40, width: 40, mr: 1.5 }} />
              </Zoom>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#1f2937", letterSpacing: 0.5, fontSize: { xs: "1.5rem", md: "2rem" } }}>
                Email Sender Pro
              </Typography>
            </Stack>

            <Typography variant="h3" fontWeight={700} gutterBottom sx={{ mb: 3, color: "#111827", fontSize: { xs: "1.75rem", md: "2.5rem" } }}>
              Elevate Your Email Campaigns
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7, maxWidth: 650, mx: "auto", fontSize: { xs: "0.95rem", md: "1.1rem" } }}>
              Unleash the power of professional bulk emailing with advanced personalization, rich media integration, and seamless Excel uploads. Perfect for businesses and marketers.
            </Typography>

            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4 }}>
              <Button variant="contained" color="primary" size="large" component={Link} to="/email-sender" sx={{ px: 4, py: 1.5, fontWeight: 600, fontSize: { xs: 14, md: 16 }, borderRadius: 2 }}>
                Get Started Now
              </Button>
              <Button variant="outlined" color="primary" size="large" component={Link} to="/about" sx={{ px: 4, py: 1.5, fontWeight: 600, fontSize: { xs: 14, md: 16 }, borderRadius: 2 }}>
                Explore Features
              </Button>
            </Stack>

            <Divider sx={{ my: 3, borderColor: "rgba(102, 126, 234, 0.2)" }} />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2}}>
              Stay Connected
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <IconButton href="https://api.whatsapp.com/send?phone=+919925615191&text=Hi+9|R,+I+want+some+more+information+about+your+services.+Please+reply+soon." target="_blank" rel="noreferrer" sx={{ color: "#25D366" }}>
                <WhatsAppIcon />
              </IconButton>
              <IconButton href="https://www.facebook.com/profile.php?id=61563096922232" target="_blank" rel="noreferrer" sx={{ color: "#1877F2" }}>
                <FacebookIcon />
              </IconButton>
              <IconButton href="https://www.instagram.com/9rnfc_com?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noreferrer" sx={{ color: "#E4405F" }}>
                <InstagramIcon />
              </IconButton>
              <IconButton href="https://www.linkedin.com/in/9r-nfc-b63aaa319" target="_blank" rel="noreferrer" sx={{ color: "#0A66C2" }}>
                <LinkedInIcon />
              </IconButton>
              <IconButton href="mailto:9rnfccard@gmail.com" sx={{ color: "#EA4335" }}>
                <MailIcon />
              </IconButton>
            </Stack>
          </Paper>

          {/* Features Section */}
          
          <Box sx={{ mt: { xs: 4, md: 6 } }}>
            <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom sx={{ mb: { xs: 3, md: 4 }, color: "#1f2937", fontSize: { xs: "1.5rem", md: "2rem" } }}>
              Why Choose Us?
            </Typography>
            <Grid container spacing={3}>
              {[
                { icon: UploadIcon, title: "Effortless Excel Uploads", desc: "Import recipient lists with ease using Excel files and customizable fields." },
                { icon: TemplateIcon, title: "Stunning Templates", desc: "Access a variety of customizable templates for every campaign need." },
                { icon: PersonIcon, title: "Personalized Outreach", desc: "Tailor emails with dynamic placeholders for a personal touch." },
                { icon: ImageIcon, title: "Rich Media Support", desc: "Embed images and resize them for perfect email delivery." },
                { icon: VideoLibraryIcon, title: "Video Embedding", desc: "Integrate videos from top platforms with thumbnail previews." },
                { icon: SendIcon, title: "Mass Sending Power", desc: "Send bulk emails with real-time tracking and analytics." },
              ].map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Zoom in timeout={index * 300 + 600}>
                    <Card sx={{ borderRadius: 2, boxShadow: 2, "&:hover": { boxShadow: 4, transform: "translateY(-4px)" }, transition: "all 0.3s ease" }}>
                      <CardContent sx={{ textAlign: "center", p: { xs: 2, md: 3 } }}>
                        <feature.icon color="primary" sx={{ fontSize: { xs: 32, md: 40 }, mb: 2 }} />
                        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: "#374151", fontSize: { xs: "1rem", md: "1.25rem" } }}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: { xs: "0.9rem", md: "1rem" } }}>
                          {feature.desc}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* CTA Section */}
          <Paper
            elevation={2}
            sx={{
              p: { xs: 3, md: 5 },
              maxWidth: 550,
              mx: "auto",
              textAlign: "center",
              borderRadius: 2,
              mt: { xs: 4, md: 6 },
              background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
              border: "1px solid rgba(102, 126, 234, 0.2)",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.05)",
              "&:hover": {
                boxShadow: "0 12px 30px rgba(0, 0, 0, 0.1)",
                transform: "translateY(-3px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <EmailIcon color="primary" sx={{ fontSize: { xs: 40, md: 48 }, mb: 3 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: "#1f2937", fontSize: { xs: "1.25rem", md: "1.75rem" } }}>
              Launch Your Campaign Today!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7, fontSize: { xs: "0.95rem", md: "1.1rem" } }}>
              Join thousands of businesses boosting their outreach with Email Sender Pro.
            </Typography>
            <Button variant="contained" color="primary" size="large" component={Link} to="/email-sender" sx={{ px: 4, py: 1.5, fontWeight: 600, fontSize: { xs: 14, md: 16 }, borderRadius: 2 }}>
              Start Your Free Trial
            </Button>
          </Paper>
        </Box>
      </Fade>
    </Box>
  );
}
